import { useEffect, useRef } from "react";

/**
 * Hook reutilizable para implementar refresh automático cada 60 segundos
 * cuando el parámetro refresh es true
 *
 * @param {boolean} refresh - Si es true, activa el refresh automático
 * @param {Function} fetchFunction - Función async para obtener datos
 * @param {Array} dependencies - Array de dependencias (matchId, etc.)
 *
 * Ejemplo de uso:
 * useAutoRefresh(refresh, async () => {
 *   const data = await getStats(matchId);
 *   setData(data);
 * }, [matchId, refresh]);
 */
export const useAutoRefresh = (refresh, fetchFunction, dependencies = []) => {
  const refreshIntervalRef = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Solo ejecutar si refresh es true
    if (!refresh) {
      // Limpiar interval si refresh es false
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      hasLoadedRef.current = false;
      return;
    }

    // Evitar ejecución duplicada
    if (hasLoadedRef.current) {
      return;
    }

    const loadInitialData = async () => {
      hasLoadedRef.current = true;

      try {
        // Cargar datos iniciales
        await fetchFunction(true); // true para mostrar loading inicial

        // Configurar actualización automática cada 60 segundos (60000 ms)
        // Sin mostrar loading para evitar saltos visuales
        refreshIntervalRef.current = setInterval(async () => {
          try {
            await fetchFunction(false); // false para no mostrar loading
          } catch (err) {
            console.error("Error en el refresh automático:", err);
          }
        }, 60000);
      } catch (err) {
        console.error("Error en la carga inicial:", err);
      }
    };

    loadInitialData();

    // Cleanup al desmontar el componente o cambiar dependencias
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      hasLoadedRef.current = false;
    };
  }, [refresh, ...dependencies]);
};
